/**
 * User Feedback Collector Component
 * Collects user feedback for post-launch optimization and improvement planning
 */

import React, { useState } from 'react';
import { errorTrackingService } from '../../services/errorTracking';

interface UserFeedbackCollectorProps {
  className?: string;
  onFeedbackSubmitted?: () => void;
  trigger?: 'button' | 'floating' | 'modal';
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
}

interface FeedbackForm {
  type: 'bug' | 'feature' | 'improvement' | 'other';
  message: string;
  email: string;
  rating: number;
  category: string;
  urgency: 'low' | 'medium' | 'high';
}

export const UserFeedbackCollector: React.FC<UserFeedbackCollectorProps> = ({
  className = '',
  onFeedbackSubmitted,
  trigger = 'button',
  position = 'bottom-right'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState<FeedbackForm>({
    type: 'improvement',
    message: '',
    email: '',
    rating: 5,
    category: 'general',
    urgency: 'medium'
  });

  const categories = {
    bug: ['UI/UX Issue', 'Performance', 'Functionality', 'Data Loss', 'Security', 'Other'],
    feature: ['New Feature', 'Enhancement', 'Integration', 'Accessibility', 'Mobile', 'Other'],
    improvement: ['User Experience', 'Performance', 'Design', 'Navigation', 'Content', 'Other'],
    other: ['General', 'Question', 'Compliment', 'Complaint', 'Suggestion', 'Other']
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!form.message.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit feedback through error tracking service
      await errorTrackingService.submitFeedback({
        type: form.type,
        message: form.message,
        email: form.email || undefined,
        additionalData: {
          rating: form.rating,
          category: form.category,
          urgency: form.urgency,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          url: window.location.href
        }
      });

      setSubmitted(true);
      
      // Reset form after delay
      setTimeout(() => {
        setForm({
          type: 'improvement',
          message: '',
          email: '',
          rating: 5,
          category: 'general',
          urgency: 'medium'
        });
        setSubmitted(false);
        setIsOpen(false);
        onFeedbackSubmitted?.();
      }, 2000);

    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof FeedbackForm, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
    
    // Reset category when type changes
    if (field === 'type') {
      setForm(prev => ({ ...prev, category: 'general' }));
    }
  };

  const getPositionClasses = () => {
    switch (position) {
      case 'bottom-right':
        return 'bottom-4 right-4';
      case 'bottom-left':
        return 'bottom-4 left-4';
      case 'top-right':
        return 'top-4 right-4';
      case 'top-left':
        return 'top-4 left-4';
      default:
        return 'bottom-4 right-4';
    }
  };

  const renderTrigger = () => {
    if (trigger === 'floating') {
      return (
        <button
          onClick={() => setIsOpen(true)}
          className={`fixed ${getPositionClasses()} z-50 bg-blue-500 hover:bg-blue-600 text-white p-3 rounded-full shadow-lg transition-all duration-200 hover:scale-110`}
          title="Send Feedback"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </button>
      );
    }

    return (
      <button
        onClick={() => setIsOpen(true)}
        className={`px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors ${className}`}
      >
        Send Feedback
      </button>
    );
  };

  const renderModal = () => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          {/* Background overlay */}
          <div 
            className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
            onClick={() => setIsOpen(false)}
          />

          {/* Modal panel */}
          <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
            {submitted ? (
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                  <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Thank you!</h3>
                <p className="text-sm text-gray-600">
                  Your feedback has been submitted successfully. We appreciate your input!
                </p>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Send Feedback</h3>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Feedback Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Feedback Type
                    </label>
                    <select
                      value={form.type}
                      onChange={(e) => handleInputChange('type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="bug">Bug Report</option>
                      <option value="feature">Feature Request</option>
                      <option value="improvement">Improvement Suggestion</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <select
                      value={form.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {categories[form.type].map((category) => (
                        <option key={category} value={category.toLowerCase().replace(/[^a-z0-9]/g, '_')}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Rating */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Overall Rating
                    </label>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => handleInputChange('rating', star)}
                          className={`text-2xl ${star <= form.rating ? 'text-yellow-400' : 'text-gray-300'} hover:text-yellow-400 transition-colors`}
                        >
                          ★
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Urgency (for bugs) */}
                  {form.type === 'bug' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Urgency
                      </label>
                      <select
                        value={form.urgency}
                        onChange={(e) => handleInputChange('urgency', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="low">Low - Minor issue</option>
                        <option value="medium">Medium - Affects functionality</option>
                        <option value="high">High - Blocks usage</option>
                      </select>
                    </div>
                  )}

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      value={form.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      placeholder={`Please describe your ${form.type === 'bug' ? 'issue' : form.type === 'feature' ? 'feature request' : 'suggestion'} in detail...`}
                      rows={4}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email (optional)
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="your.email@example.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Provide your email if you'd like us to follow up with you
                    </p>
                  </div>

                  {/* Submit Button */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setIsOpen(false)}
                      className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting || !form.message.trim()}
                      className="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white rounded-md transition-colors"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Feedback'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {renderTrigger()}
      {renderModal()}
    </>
  );
};

export default UserFeedbackCollector;